import { useState, type FormEvent, type ReactNode } from 'react';
import { MIN_PASSWORD, isValidEmail, isValidUsername, normUser } from '@aop/domain';
import { IconLogo } from '../ui/icons';
import { PasswordInput } from '../ui/PasswordInput';
import { useAuth } from './AuthContext';

export function Card({ children, loading }: { children?: ReactNode; loading?: string }) {
  return (
    <div className="auth">
      <div className="auth-card">
        <div className="brand">
          <div className="mark" aria-hidden="true"><IconLogo /></div>
          <div><h1>Cổng nghiệp vụ Kế toán</h1><small>Kế toán – Tài chính</small></div>
        </div>
        {loading && <p className="hint">{loading}</p>}
        {children}
      </div>
    </div>
  );
}

export const Field = ({ id, label, type, ...rest }: { id: string; label: string } & React.InputHTMLAttributes<HTMLInputElement>) => (
  <div className="f"><label htmlFor={id}>{label}</label>
    {type === 'password' ? <PasswordInput id={id} {...rest} /> : <input id={id} type={type} {...rest} />}
  </div>
);

export function LoadingScreen() { return <Card loading="Đang kết nối dữ liệu…" />; }

export function LoginScreen({ message }: { message?: string }) {
  const { login } = useAuth();
  const [user, setUser] = useState(''), [pw, setPw] = useState(''), [err, setErr] = useState(message || ''), [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setErr('');
    const m = await login(user, pw);
    if (m) { setErr(m); setPw(''); }
    setBusy(false);
  };
  return (
    <Card>
      <form onSubmit={submit}>
        <h2>Đăng nhập</h2>
        <Field id="liUser" label="Tên đăng nhập" value={user} onChange={e => setUser(e.target.value)} autoComplete="username" autoCapitalize="none" spellCheck={false} autoFocus />
        <Field id="liPw" label="Mật khẩu" type="password" value={pw} onChange={e => setPw(e.target.value)} autoComplete="current-password" />
        <p className="auth-err" role="alert">{err}</p>
        <button type="submit" className="btn primary" disabled={busy}>{busy ? 'Đang đăng nhập…' : 'Đăng nhập'}</button>
      </form>
    </Card>
  );
}

export function SetupScreen() {
  const { setupAdmin } = useAuth();
  const [f, setF] = useState({ fullName: '', username: '', email: '', pw: '', pw2: '', token: '' });
  const [err, setErr] = useState(''), [busy, setBusy] = useState(false), [needToken, setNeedToken] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF(s => ({ ...s, [k]: e.target.value }));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const un = normUser(f.username);
    if (!f.fullName.trim() || !un || !f.email.trim()) return setErr('Nhập đủ họ tên, tên đăng nhập và email.');
    if (!isValidUsername(un)) return setErr('Tên đăng nhập 3–32 ký tự: chữ thường không dấu, số, dấu chấm, gạch.');
    if (!isValidEmail(f.email.trim())) return setErr('Email không hợp lệ.');
    if (f.pw.length < MIN_PASSWORD) return setErr(`Mật khẩu tối thiểu ${MIN_PASSWORD} ký tự.`);
    if (f.pw !== f.pw2) return setErr('Hai lần nhập mật khẩu không khớp.');
    setBusy(true); setErr('');
    const m = await setupAdmin({ fullName: f.fullName.trim(), username: un, email: f.email.trim(), password: f.pw, token: f.token || undefined });
    if (m) { setErr(m); if (/Mã thiết lập/.test(m)) setNeedToken(true); }
    setBusy(false);
  };
  return (
    <Card>
      <form onSubmit={submit}>
        <h2>Tạo tài khoản quản trị đầu tiên</h2>
        <p className="hint">Chưa có người dùng nào. Tài khoản này có toàn quyền và quản lý người dùng khác.</p>
        <Field id="suName" label="Họ tên" value={f.fullName} onChange={set('fullName')} autoComplete="name" />
        <Field id="suUser" label="Tên đăng nhập" value={f.username} onChange={set('username')} autoComplete="username" autoCapitalize="none" spellCheck={false} />
        <Field id="suEmail" label="Email" type="email" value={f.email} onChange={set('email')} autoComplete="email" />
        <Field id="suPw" label={`Mật khẩu (tối thiểu ${MIN_PASSWORD} ký tự)`} type="password" value={f.pw} onChange={set('pw')} autoComplete="new-password" />
        <Field id="suPw2" label="Nhập lại mật khẩu" type="password" value={f.pw2} onChange={set('pw2')} autoComplete="new-password" />
        {needToken && <Field id="suToken" label="Mã thiết lập (do người triển khai cung cấp)" value={f.token} onChange={set('token')} autoComplete="off" />}
        <p className="auth-err" role="alert">{err}</p>
        <button type="submit" className="btn primary" disabled={busy}>{busy ? 'Đang tạo…' : 'Tạo tài khoản và đăng nhập'}</button>
      </form>
    </Card>
  );
}

export function ForceChangeScreen() {
  const { setNewPassword, logout } = useAuth();
  const [pw, setPw] = useState(''), [pw2, setPw2] = useState(''), [err, setErr] = useState(''), [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (pw.length < MIN_PASSWORD) return setErr(`Mật khẩu tối thiểu ${MIN_PASSWORD} ký tự.`);
    if (pw !== pw2) return setErr('Hai lần nhập mật khẩu không khớp.');
    setBusy(true); setErr('');
    const m = await setNewPassword(pw);
    if (m) setErr(m);
    setBusy(false);
  };
  return (
    <Card>
      <form onSubmit={submit}>
        <h2>Đổi mật khẩu</h2>
        <p className="hint">Mật khẩu hiện tại là mật khẩu tạm. Hãy đặt mật khẩu mới trước khi tiếp tục.</p>
        <Field id="fpPw" label={`Mật khẩu mới (tối thiểu ${MIN_PASSWORD} ký tự)`} type="password" value={pw} onChange={e => setPw(e.target.value)} autoComplete="new-password" autoFocus />
        <Field id="fpPw2" label="Nhập lại mật khẩu mới" type="password" value={pw2} onChange={e => setPw2(e.target.value)} autoComplete="new-password" />
        <p className="auth-err" role="alert">{err}</p>
        <button type="submit" className="btn primary" disabled={busy}>Lưu mật khẩu mới</button>
        <button type="button" className="linkbtn" onClick={() => void logout()}>Đăng xuất</button>
      </form>
    </Card>
  );
}

export function ConfigErrorScreen() {
  return (
    <Card>
      <h2>Thiếu cấu hình kết nối</h2>
      <p className="hint">Ứng dụng chưa biết địa chỉ Supabase và API. Khi chạy thử, tạo file <code>apps/web/.env.local</code> từ <code>.env.example</code> rồi khởi động lại. Khi triển khai, đặt biến môi trường theo hướng dẫn trong tài liệu triển khai.</p>
    </Card>
  );
}
